import React, { useEffect, useState } from 'react';
import {
	DropDown,
	LightningBoltFilled,
	ListGold,
	ListWhite,
	LocationGold,
	LocationWhite,
	PlaneEmptyYellow,
	PlaneWhite,
	PlusGold,
	SearchIcon
} from '@/resources/assets/images';
import { sendAnalytics } from '@/services/analyticsService';
import type { List, Location } from '@/modules/dashboard/dashboardTypes';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import Link from '@/components/Link/Link';
import { useLocation } from 'react-router-dom';
import { useNavBarContext } from './NavBarContext';
import './NavBar.scss';

type NavOptions = 'Lists' | 'Locations';

interface Props {
	selectedLocationFilter: string;
	setSelectedLocationFilter: (x: string) => void;
	selectedListFilter: string;
	setSelectedListFilter: (x: string) => void;
	setAddLocationOpen: (x: boolean) => void;
	closeLocationDropdown: () => void;
	locationChange: (location: string) => void;
	listChange: (list: string) => void;
}

const NavBar = ({
	selectedLocationFilter,
	setSelectedLocationFilter,
	selectedListFilter,
	setSelectedListFilter,
	setAddLocationOpen,
	closeLocationDropdown,
	locationChange,
	listChange
}: Props): JSX.Element => {
	const [lists, selectedList, locations, selectedLocation, user] =
		useSelector(
			(state: RootState) => [
				state.dashboard.getIn(['lists', 'data']).toJS(),
				state.dashboard.getIn(['lists', 'selected']).toJS(),
				state.dashboard.getIn(['locations', 'data']).toJS(),
				state.dashboard.getIn(['locations', 'selected']).toJS(),
				state.user.getIn(['authorize', 'user']).toJS()
			],
			shallowEqual
		);
	const [searchFocused, setSearchFocused] = useState<boolean>(false);
	const { hovered, setHovered } = useNavBarContext();
	const onHover = (x: NavOptions | undefined) => setHovered(x);

	const location = useLocation();

	const openFilter = e => {
		if (e && e.classList.contains('input-open')) {
			e.classList.remove('input-open');
		} else {
			e.classList.add('input-open');
		}
	};

	const closeListDropdown = () => {
		const el = document.getElementById('header-input-list-element');
		if (el) {
			el.classList.remove('input-open');
		}
	};

	const displayLocationDropdownText = () => {
		if (hovered === 'Locations') {
			return selectedLocation.name
				? selectedLocation.name
				: 'Select Location';
		}

		return 'Locations';
	};

	const displayListDropdownText = () => {
		if (hovered === 'Lists') {
			return selectedList.name ? selectedList.name : 'Select a List';
		}

		return 'Lists';
	};

	// Alright, this is a hack. There's no way to do this because the state is local and not a context
	// The list keep selected though, cause if we call changeList('') here it breaks.
	useEffect(() => {
		// Handles when going from list to sequences
		if (location.pathname === '/sequences') {
			setSelectedListFilter('');
		}
	}, [location.pathname === '/sequences']);

	const searchFilter = place => {
		let input;
		let filter;
		let listNames;
		let i;
		let div;
		if (place === 'list') {
			input = document.getElementById('myInputList');
			filter = input.value.toUpperCase();
			div = document.getElementById('header-list-dropdown');
			listNames = div.getElementsByClassName('header-list-row-title');
			var searchResults = div.getElementsByClassName('header-list-row');

			for (i = 0; i < listNames.length; i++) {
				var txtValue =
					listNames[i].textContent || listNames[i].innerText;

				if (txtValue.toUpperCase().indexOf(filter) > -1) {
					listNames[i].style.display = '';
					searchResults[i].style.display = '';
				} else {
					listNames[i].style.display = 'none';
					searchResults[i].style.display = 'none';
				}
			}
		}
		if (place === 'location') {
			input = document.getElementById('myInputLocation');
			filter = input.value.toUpperCase();
			div = document.getElementById('header-location-dropdown');
			listNames = div.getElementsByClassName('header-location-row-title');
			var searchResults = div.getElementsByClassName(
				'header-location-row'
			);

			for (i = 0; i < listNames.length; i++) {
				var txtValue =
					listNames[i].textContent || listNames[i].innerText;

				if (txtValue.toUpperCase().indexOf(filter) > -1) {
					listNames[i].style.display = '';
					searchResults[i].style.display = '';
				} else {
					listNames[i].style.display = 'none';
					searchResults[i].style.display = 'none';
				}
			}
		}
	};

	const sequenceClickHandler = () => {
		setSelectedLocationFilter('');
		setSelectedListFilter('');
		locationChange('');
		listChange('');

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: 'user_accessed_sequences_page'
		});
	};

	return (
		<div className="header-input-wrapper joyride-step">
			<div
				id="header-input-location-element"
				className={`header-input locations ${searchFocused ? 'shrinked' : ''} ${hovered === 'Locations' ? 'hovered' : ''} ${selectedLocationFilter ? 'selected' : ''} `}
				onClick={e => {
					setSearchFocused(false);
					const tar = e.currentTarget;
					setTimeout(() => {
						openFilter(tar);
					}, 0);
				}}
				onMouseEnter={() => onHover('Locations')}
				onMouseLeave={() => onHover(undefined)}
			>
				<div className="header-input-inner-div">
					<span className="header-input-locations-hovered">
						{hovered === 'Locations' || selectedLocationFilter ? (
							<LocationGold className="header-input-locations-icon" />
						) : (
							<LocationWhite className="header-input-locations-icon" />
						)}
						<span className="header-input-locations-header">
							{displayLocationDropdownText()}
						</span>
						{hovered === 'Locations' && (
							<DropDown className="header-icon-chevron" />
						)}
					</span>
				</div>
				<div className="header-dropdown-bg" />
				<div
					className="header-location-dropdown"
					onClick={e => {
						e.stopPropagation();
					}}
					id="header-location-dropdown"
				>
					<div className="header-search">
						<input
							className="search-input-header w-full text-white"
							type="text"
							placeholder="Search"
							id="myInputLocation"
							autoComplete="off"
							onKeyUp={() => searchFilter('location')}
						/>
						<div className="d-inline-block search-icon absolute top-1/2 -translate-y-1/2 transform">
							<SearchIcon className="header-search-icon" />
						</div>
					</div>
					<div className="header-items">
						<div
							className="add-location-row"
							onClick={() => {
								setAddLocationOpen(true);
								closeLocationDropdown();
								setSelectedLocationFilter('');
								locationChange('');
								sendAnalytics({
									type: 'track',
									userId: user.email,
									eventName:
										'User clicked the "Add New Location" button in the locations dropdown'
								});
							}}
						>
							<PlusGold className="add-location-row-icon" />
							Add New Location
						</div>
						{locations &&
							locations.map((location: Location) => (
								<div
									className={`header-location-row ${selectedLocationFilter === location.id ? 'selected' : ''} `}
									key={location.id ?? Math.random()}
									onClick={e => {
										closeLocationDropdown();

										if (
											selectedLocationFilter !==
											location.id
										) {
											setSelectedLocationFilter(
												location.id
											);
											locationChange(location.id);
										}

										setSelectedListFilter('');
									}}
									id="location-dropdown"
								>
									<div className="header-location-row-title">
										{location.name}
									</div>
								</div>
							))}
					</div>
				</div>
			</div>
			<div
				id="header-input-list-element"
				className={`header-input lists ${searchFocused ? 'shrinked' : ''} ${hovered === 'Lists' ? 'hovered' : ''} ${selectedListFilter ? 'selected' : ''} `}
				onClick={e => {
					setSearchFocused(false);
					const tar = e.currentTarget;
					setTimeout(() => {
						openFilter(tar);
					}, 0);
				}}
				onMouseEnter={() => onHover('Lists')}
				onMouseLeave={() => onHover(undefined)}
			>
				<div className="header-input-inner-div">
					<span className="header-input-lists-hovered">
						{hovered === 'Lists' || selectedListFilter ? (
							<ListGold className="header-input-lists-icon" />
						) : (
							<ListWhite className="header-input-lists-icon" />
						)}
						<span
							className="header-input-lists-header"
							id="list-header"
						>
							{displayListDropdownText()}
						</span>
						{hovered === 'Lists' && (
							<DropDown className="header-icon-chevron" />
						)}
					</span>
				</div>
				<div className="header-dropdown-bg" />
				<div
					className="header-list-dropdown"
					onClick={e => {
						e.stopPropagation();
					}}
					id="header-list-dropdown"
				>
					<div className="header-search">
						<input
							className="search-input-header w-full text-white"
							type="text"
							placeholder="Search"
							id="myInputList"
							autoComplete="off"
							onKeyUp={() => searchFilter('list')}
						/>
						<div className="d-inline-block search-icon absolute top-1/2 -translate-y-1/2 transform">
							<SearchIcon className="header-search-icon" />
						</div>
					</div>
					<div className="header-items">
						{lists &&
							lists.map((list: List) => (
								<div
									className={`header-list-row ${selectedListFilter === list.id ? 'selected' : ''} `}
									key={list.id ?? Math.random()}
									onClick={e => {
										setSelectedLocationFilter('');

										if (selectedListFilter !== list.id) {
											setSelectedListFilter(list.id);
											listChange(list.id);
										}

										closeListDropdown();
									}}
									id="list-dropdown"
								>
									<div className="header-list-row-title">
										{list.name}
									</div>
								</div>
							))}
					</div>
				</div>
			</div>
			{(user?.threadedEmail ||
				user?.re2HostedEmail ||
				(user.re2SharedDomain &&
					user?.features?.sharedDomainVersion !== 'v2')) && (
				<Link
					className={`header-input campaigns ${
						location.pathname === '/sequences' ? 'selected' : ''
					}`}
					to="/sequences"
					onClick={sequenceClickHandler}
				>
					<>
						{location.pathname === '/sequences' ? (
							<PlaneEmptyYellow className="header-input-campaigns-icon" />
						) : (
							<PlaneWhite className="header-input-campaigns-icon" />
						)}
						Sequences
					</>
				</Link>
			)}
		</div>
	);
};

export default NavBar;
